import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Row,
  Col,
  InputNumber,
  message,
  Descriptions,
  Typography,
  Alert,
  Statistic,
  Progress,
  Badge,
  List,
  Timeline,
  Steps,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  ScanOutlined,
  BarcodeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  WarningOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import moment from 'moment';
import apiService from '../../../../services/apiService';
import { useNavigate } from 'react-router-dom';
import { playErrorSound } from '../../../../utils/soundUtils';
import type { 
  BatchPickingList, 
  ConsolidatedPickingItem,
  ScanResult,
  PickingDiscrepancy
} from '../../types/picking';

const { Title, Text } = Typography;
const { Step } = Steps;

interface BarcodeScanningInterfaceProps {
  batchPickingList: BatchPickingList;
  onUpdateBatchPicking: (updated: BatchPickingList) => void;
  onClose?: () => void;
  kpi?: { processing: number; allocating: number; verifying: number };
  clearHistoryTrigger?: number;
}

const BarcodeScanningInterface: React.FC<BarcodeScanningInterfaceProps> = ({
  batchPickingList,
  onUpdateBatchPicking,
  // onClose
  kpi,
  clearHistoryTrigger
}) => {
  const navigate = useNavigate();
  const [currentBatch, setCurrentBatch] = useState<BatchPickingList>(batchPickingList);
  const [scanMode, setScanMode] = useState<'picking' | 'verification'>('picking');
  const [scanning, setScanning] = useState(true);
  // const [currentItem, setCurrentItem] = useState<ConsolidatedPickingItem | null>(null);
  const [lastScannedId, setLastScannedId] = useState<string | null>(null);
  const [allScanned, setAllScanned] = useState(false); // 自動建包裝使用
  // const [scannedItems, setScannedItems] = useState<string[]>([]);
  const [scanHistory, setScanHistory] = useState<ScanResult[]>([]);
  // 由 HKTV 運單掃描取得的對應 orderNumber（orderId）
  const [currentWaybillOrder, setCurrentWaybillOrder] = useState<string | null>(null);
  const [currentWaybillNumber, setCurrentWaybillNumber] = useState<string | null>(null);
  const [waybillOrderProducts, setWaybillOrderProducts] = useState<any[]>([]);
  // const [pickingOperations] = useState<PickingOperation[]>([]);
  const [discrepancyModalVisible, setDiscrepancyModalVisible] = useState(false);
  const [currentDiscrepancy, setCurrentDiscrepancy] = useState<Partial<PickingDiscrepancy> | null>(null);
  const [scanBuffer, setScanBuffer] = useState('');
  const [lastScanTime, setLastScanTime] = useState(0);
  const [isRealScannerMode, setIsRealScannerMode] = useState(false);
  const [manualScanValue, setManualScanValue] = useState('');
  
  const scanInputRef = useRef<any>(null);
  // 首次揀貨掃描：將該批次涉及的出庫單狀態改為「配貨中」（ALLOCATING）
  const didMarkAllocatingRef = useRef<boolean>(false);
  const ensureOutboundAllocatingOnce = async (opts?: { orderIds?: string[]; orderNumbers?: string[] }) => {
    if (didMarkAllocatingRef.current) return;
    try {
      const providedIds = Array.isArray(opts?.orderIds) ? opts!.orderIds!.filter(Boolean) : [];
      const providedNumbers = Array.isArray(opts?.orderNumbers) ? opts!.orderNumbers!.filter(Boolean) : [];
      let ids: string[] = providedIds;
      let numbers: string[] = providedNumbers;
      if (ids.length === 0 && numbers.length === 0) {
        const ident = currentBatch.id || currentBatch.batchPickingNumber;
        if (ident) {
          const ordersResp: any = await apiService.getPickingOrders(String(ident)).catch(() => null);
          const orders: Array<any> = (ordersResp && (ordersResp.data ?? ordersResp)) || [];
          ids = orders.map(o => String(o.id || '')).filter(Boolean);
          numbers = orders.map(o => String(o.orderNumber || o.outbound_number || '')).filter(Boolean);
        }
      }
      if (ids.length > 0) {
        await apiService.batchUpdateOutboundStatus({ ids, status: 'ALLOCATING' });
      }
      for (const num of (ids.length ? [] : numbers)) {
        try { await apiService.updateOutboundOrder(num, { status: 'ALLOCATING' }); } catch {}
      }
    } catch {
      // 同步失敗不影響掃描流程
    } finally {
      didMarkAllocatingRef.current = true;
    }
  };
  // 首次達到「已拣滿」時：將出庫單狀態改為「核對中」（VERIFYING）
  const didMarkVerifyingRef = useRef<boolean>(false);
  const ensureOutboundVerifyingOnce = async (opts?: { orderIds?: string[]; orderNumbers?: string[] }) => {
    if (didMarkVerifyingRef.current) return;
    try {
      const providedIds = Array.isArray(opts?.orderIds) ? opts!.orderIds!.filter(Boolean) : [];
      const providedNumbers = Array.isArray(opts?.orderNumbers) ? opts!.orderNumbers!.filter(Boolean) : [];
      let ids: string[] = providedIds;
      let orderNumbers: string[] = providedNumbers;
      if (ids.length === 0 && orderNumbers.length === 0) {
        const ident = currentBatch.id || currentBatch.batchPickingNumber;
        if (ident) {
          const ordersResp: any = await apiService.getPickingOrders(String(ident)).catch(() => null);
          const orders: Array<any> = (ordersResp && (ordersResp.data ?? ordersResp)) || [];
          ids = orders.map(o => String(o.id || '')).filter(Boolean);
          orderNumbers = orders.map(o => String(o.orderNumber || o.outbound_number || '')).filter(Boolean);
        }
      }
      if (ids.length > 0) {
        try { await apiService.batchUpdateOutboundStatus({ ids, status: 'VERIFYING' }); } catch {}
      }
      for (const ident2 of (ids.length ? [] : orderNumbers)) {
        try { await apiService.updateOutboundOrder(ident2, { status: 'VERIFYING' }); } catch {}
      }
    } catch {
      // 忽略錯誤，避免影響 UI
    } finally {
      didMarkVerifyingRef.current = true;
    }
  };
  const itemRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [allowedBarcodeToSku, setAllowedBarcodeToSku] = useState<Record<string, string>>({});
  const [allowedSkuToMeta, setAllowedSkuToMeta] = useState<Record<string, { name: string; barcode: string | null; expectedQty: number; originalSku?: string }>>({});
  const [allowedBarcodeSet, setAllowedBarcodeSet] = useState<Set<string>>(new Set());
  // 輕量標準化：僅去除首尾空白，保留所有字符（下劃線等）以實現精確匹配
  const normalize = (v?: string | null) => String(v ?? '').trim();
  const [form] = Form.useForm();

  const handleClearScanHistory = () => {
    setScanHistory([]);
    message.success('已清空掃描歷史');
  };

  useEffect(() => {
    if (typeof clearHistoryTrigger === 'number') {
      // 每次 trigger 改變即清空
      setScanHistory([]);
    }
  }, [clearHistoryTrigger]);

  // 確保白名單就緒：若為空則拉取 print-data 建立映射，並返回最新映射
  const ensureWhiteListReady = async (): Promise<{ bc: Record<string, string>; sku: Record<string, { name: string; barcode: string | null; expectedQty: number }> }> => {
    let bc = allowedBarcodeToSku;
    let sku = allowedSkuToMeta as Record<string, { name: string; barcode: string | null; expectedQty: number }>;
    if (bc && Object.keys(bc).length > 0 && sku && Object.keys(sku).length > 0) {
      console.log('[SCAN] whitelist ready (cached): bcSize=', Object.keys(bc).length, 'skuSize=', Object.keys(sku).length);
      return { bc, sku };
    }
    try {
      const ident = currentBatch.id || currentBatch.batchPickingNumber;
      if (!ident) return { bc: {}, sku: {} };
      const resp: any = await apiService.getPickingPrintData(String(ident));
      const items: Array<any> = resp?.data?.items || resp?.items || [];
      console.log('[SCAN] fetched print-data items=', items.length);
      const bcToSku: Record<string, string> = {};
      const skuToMeta: Record<string, { name: string; barcode: string | null; expectedQty: number }> = {};
      for (const it of items) {
        const s = normalize(it.sku || it.productSku);
        const b = normalize(it.productBarcode);
        const q = Number(it.quantity || 0);
        if (s) skuToMeta[s] = { name: String(it.product || it.productName || s), barcode: b || null, expectedQty: q };
        if (b && s) bcToSku[b] = s;
      }
      // 後備：若白名單為空或條碼缺失，基於出庫明細+套裝展開構建
      if (Object.keys(skuToMeta).length === 0) {
        try {
          const ordersResp: any = await apiService.getPickingOrders(String(ident)).catch(() => null);
          const orders: Array<any> = (ordersResp && (ordersResp.data ?? ordersResp)) || [];
          const orderItems: Array<{ sku: string; qty: number; name?: string }> = [];
          for (const o of orders) {
            const its = Array.isArray(o.items) ? o.items : [];
            for (const i of its) {
              const s0 = normalize(i.productSku || i.sku || i.product_sku || '');
              if (!s0) continue;
              orderItems.push({ sku: s0, qty: Number(i.quantity || i.requestedQuantity || 0) || 0, name: i.productName || i.name });
            }
          }
          const uniqueSkus = Array.from(new Set(orderItems.map(i => i.sku)));
          const skuToQty = new Map<string, number>();
          orderItems.forEach(i => skuToQty.set(i.sku, (skuToQty.get(i.sku) || 0) + (i.qty || 0)));
          const skuToProduct: Record<string, { id: string; name: string; barcode: string | null; isBundle?: boolean }> = {} as any;
          for (const code of uniqueSkus) {
            try {
              const pList: any = await apiService.getProductsV2({ sku: code, limit: 1 }).catch(() => null);
              const p = Array.isArray(pList?.products) && pList.products.length > 0 ? pList.products[0] : null;
              if (p) {
                skuToProduct[code] = { id: String(p.id || p.productId || ''), name: String(p.itemName || p.name || code), barcode: p.barcode || null, isBundle: !!(p.isBundle || p.is_bundle) };
              }
            } catch {}
          }
          for (const code of uniqueSkus) {
            const prod = skuToProduct[code];
            const baseQty = skuToQty.get(code) || 0;
            if (!prod) continue;
            if (prod.isBundle) {
              try {
                const full: any = await apiService.getProductByIdV2(prod.id).catch(() => null);
                const bItems: Array<any> = Array.isArray((full as any)?.bundleItems) ? (full as any).bundleItems as any[] : [];
                for (const bi of bItems) {
                  const cSku = normalize(bi.sku || bi.productId || '');
                  if (!cSku) continue;
                  let compBarcode: string | null = null;
                  try {
                    const compList: any = await apiService.getProductsV2({ sku: cSku, limit: 1 }).catch(() => null);
                    const cp = Array.isArray(compList?.products) && compList.products.length > 0 ? compList.products[0] : null;
                    compBarcode = cp?.barcode || null;
                  } catch {}
                  if (!skuToMeta[cSku]) skuToMeta[cSku] = { name: String(bi.productName || cSku), barcode: compBarcode ? normalize(compBarcode) : null, expectedQty: 0 };
                  skuToMeta[cSku].expectedQty += Math.max(1, Number(bi.quantity || 1)) * Math.max(1, baseQty || 1);
                  if (compBarcode) bcToSku[normalize(compBarcode)] = cSku;
                }
              } catch {}
            } else {
              if (!skuToMeta[code]) skuToMeta[code] = { name: prod.name, barcode: prod.barcode ? normalize(prod.barcode) : null, expectedQty: 0 };
              skuToMeta[code].expectedQty += Math.max(1, baseQty || 1);
              if (prod.barcode) bcToSku[normalize(prod.barcode)] = code;
            }
          }
        } catch (e) {
          console.warn('[SCAN] fallback build whitelist failed:', e);
        }
      }
      setAllowedBarcodeToSku(bcToSku);
      setAllowedSkuToMeta(skuToMeta);
      setAllowedBarcodeSet(new Set(Object.keys(bcToSku)));
      console.log('[SCAN] whitelist built: bcSize=', Object.keys(bcToSku).length, 'skuSize=', Object.keys(skuToMeta).length);
      return { bc: bcToSku, sku: skuToMeta };
    } catch {
      return { bc: {}, sku: {} };
    }
  };

  useEffect(() => {
    // 若從列表點擊掃描而僅帶參數（無 props），嘗試從 URL 解析批次號/ID
    try {
      const hasIdent = Boolean(currentBatch.id || currentBatch.batchPickingNumber);
      if (!hasIdent) {
        const qs = new URLSearchParams(window.location.search);
        const fromParam = qs.get('batch') || qs.get('id') || qs.get('batchNumber') || '';
        if (fromParam) {
          setCurrentBatch(prev => ({
            ...prev,
            id: prev.id || fromParam,
            batchPickingNumber: prev.batchPickingNumber || fromParam,
            updatedAt: new Date()
          } as any));
        }
      }
    } catch {}

    // 讀取已保存的掃描進度並還原 UI（當批次 id/編號改變時也會觸發）
    (async () => {
      try {
        const ident = currentBatch.id || currentBatch.batchPickingNumber;
        if (!ident) return;
        const resp: any = await apiService.getPickingScanItems(String(ident)).catch(() => null);
        const rows: any[] = (resp && (resp.data ?? resp)) || [];
        if (Array.isArray(rows) && rows.length > 0) {
          const restored = rows.map(r => ({
            productId: String(r.sku || ''),
            productSku: String(r.sku || ''),
            productNameEn: '',
            productNameCn: '',
            productBarcode: r.barcode || '',
            totalQuantity: Number(r.totalQty || 0),
            pickedQuantity: Number(r.pickedQty || 0),
            confirmedQuantity: Number(r.confirmedQty || 0),
            locationFrom: currentBatch.locationFrom || '主倉庫',
            locationTo: currentBatch.locationTo || '打包區',
            lotNumbers: [],
            serialNumbers: [],
            expirationDates: [],
            sourceOrders: [],
            hasDiscrepancy: false
          }));
          setCurrentBatch(prev => ({ ...prev, consolidatedItems: restored, updatedAt: new Date() } as any));
          // 還原掃描歷史（簡版：顯示還原記錄）
          const restoreLogs = rows.map(r => ({
            barcode: r.barcode || r.sku,
            type: 'product',
            success: true,
            data: { sku: r.sku, picked: r.pickedQty, total: r.totalQty } as any,
            timestamp: new Date(r.updatedAt || Date.now())
          }));
          setScanHistory(restoreLogs as any);
        }
      } catch {}
    })();

    // Focus input
    if (scanInputRef.current) scanInputRef.current.focus();
  }, [currentBatch.id, currentBatch.batchPickingNumber]);

  // 初始化/批次切換/保存進度後：讀取本批次允許的產品清單（從後端 PrintData）
  useEffect(() => {
    (async () => {
      try {
        const ident = currentBatch.id || currentBatch.batchPickingNumber;
        if (!ident) return;
        const resp: any = await apiService.getPickingPrintData(String(ident));
        const items: Array<any> = resp?.data?.items || resp?.items || [];
        if (!Array.isArray(items)) return;
        const bcToSku: Record<string, string> = {};
        const skuToMeta: Record<string, { name: string; barcode: string | null; expectedQty: number; originalSku?: string }> = {};
        const bcSet = new Set<string>();
        items.forEach((it: any) => {
          const rawSku = String(it.sku || it.productSku || '');
          const sku = normalize(rawSku);
          const bc = normalize(it.productBarcode);
          // 需求改為默認 1：只要存在於白名單，即視為需要 1 件
          const qty = 1;
          if (sku) skuToMeta[sku] = { name: String(it.product || it.productName || rawSku), barcode: bc || null, expectedQty: qty, originalSku: rawSku };
          if (bc) { bcToSku[bc] = sku; bcSet.add(bc); }
        });
        setAllowedBarcodeToSku(bcToSku);
        setAllowedSkuToMeta(skuToMeta);
        setAllowedBarcodeSet(bcSet);
      } catch (err) {
        // 忽略初始化錯誤，允許掃描流程繼續
      }
    })();
  }, [currentBatch.id, currentBatch.batchPickingNumber]);

  // 每次產品列表或數量變化，檢查是否已全部掃描完
  useEffect(() => {
    const items = currentBatch.consolidatedItems || [];
    if (items.length === 0) { setAllScanned(false); return; }
    // 僅以「有需求數量的項目」作為是否已拣滿的依據，避免 0 導致誤判
    const requiredItems = items.filter(i => (Number(i.totalQuantity) || 0) > 0);
    const requiredTotal = requiredItems.reduce((sum, i) => sum + (Number(i.totalQuantity) || 0), 0);
    const done = requiredTotal > 0 && requiredItems.every(i => (Number(i.pickedQuantity) || 0) >= (Number(i.totalQuantity) || 0));
    setAllScanned(done);
    // 由使用者點擊「開始包裝」按鈕觸發，不再自動建立包裝
  }, [currentBatch.consolidatedItems, manualScanValue, scanHistory.length]);

  // 當達到「已拣滿」狀態時，將 Outbound 狀態由「配貨中」切到「核對中」
  // 已移除：不再用 allScanned 觸發，僅使用 UI 顯示邏輯 isAllScanned 觸發

  // 僅同步必要頭部字段到父組件，避免用父組件舊值覆蓋子組件的 consolidatedItems
  useEffect(() => {
    if (!onUpdateBatchPicking) return;
    const { id, status, updatedAt, batchPickingNumber, batchPickingBarcode } = currentBatch;
    onUpdateBatchPicking({ id, status, updatedAt, batchPickingNumber, batchPickingBarcode } as any);
  }, [currentBatch.id, currentBatch.status, currentBatch.updatedAt, currentBatch.batchPickingNumber, currentBatch.batchPickingBarcode]);

  // Hardware barcode scanner integration
  useEffect(() => {
    let scannerTimeout: NodeJS.Timeout;
    
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const currentTime = Date.now();
      
      // Check if this could be from a barcode scanner (rapid input)
      if (currentTime - lastScanTime < 100) {
        setIsRealScannerMode(true);
      }
      
      setLastScanTime(currentTime);

      // Handle Enter key (scanner suffix)
      if (event.key === 'Enter') {
        event.preventDefault();
        if (scanBuffer.trim().length > 0) {
          handleScan(scanBuffer.trim());
          setScanBuffer('');
          setIsRealScannerMode(false);
        }
        return;
      }

      // Handle regular characters (build barcode)
      if (event.key.length === 1) {
        event.preventDefault();
        setScanBuffer(prev => prev + event.key);
        
        // Clear buffer after delay (in case scanner doesn't send Enter)
        clearTimeout(scannerTimeout);
        scannerTimeout = setTimeout(() => {
          if (scanBuffer.length > 8) { // Minimum barcode length
            handleScan(scanBuffer.trim());
          }
          setScanBuffer('');
          setIsRealScannerMode(false);
        }, 300);
      }
    };

    // Add global keyboard listener for barcode scanner
    if (scanning) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(scannerTimeout);
    };
  }, [scanning, scanBuffer, lastScanTime]);

  // Visual feedback for scanner detection
  useEffect(() => {
    if (isRealScannerMode && scanBuffer.length > 0) {
      // Show visual feedback that scanner is detected
      const indicator = document.createElement('div');
      indicator.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #52c41a;
        color: white;
        padding: 8px 16px;
        border-radius: 4px;
        z-index: 9999;
        font-size: 14px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      `;
      indicator.textContent = `🔍 掃描中... ${scanBuffer}`;
      document.body.appendChild(indicator);
      
      setTimeout(() => {
        if (document.body.contains(indicator)) {
          document.body.removeChild(indicator);
        }
      }, 2000);
    }
  }, [isRealScannerMode, scanBuffer]);

  // 需求更新：不再掃描運單號（移除未用函數）

  const handleProductScan = async (barcode: string) => {
    // 首次掃描產品時，將相關出庫單設為「配貨中」
    await ensureOutboundAllocatingOnce();
    const { bc, sku } = await ensureWhiteListReady();
    const normalized = normalize(barcode);
    console.log('[SCAN] start pick: raw=', barcode, 'normalized=', normalized, 'listSize=', (currentBatch.consolidatedItems || []).length);
    
    // 新邏輯（運單模式）：掃描產品直接在對應行「確認 +1」，直至達到已拣數
    if (currentWaybillOrder && waybillOrderProducts.length > 0) {
      const itemsNow = Array.isArray(currentBatch.consolidatedItems) ? currentBatch.consolidatedItems : [];
      const idx = itemsNow.findIndex(it => {
        const barcodes = it.productBarcode
            ? it.productBarcode.split(',')  // 先分割多個條碼
            : [];

        return (
            barcodes.some(bc => normalize(bc) === normalized)
        );
      });
      /*const idx = itemsNow.findIndex(it =>
        normalize(it.productBarcode) === normalized || normalize(it.productSku) === normalized
      );*/
      if (idx < 0) {
        playErrorSound();
        message.error(`此產品不屬於運單 ${currentWaybillOrder}`);
        setScanHistory([{
          barcode,
          type: 'product',
          success: false,
          error: `❌ 產品不匹配\n此產品不屬於運單：${currentWaybillOrder}\n請掃描正確的產品條碼`,
          timestamp: new Date()
        }, ...scanHistory]);
        return;
      }

      let updatedItems: any[] = [];
      let updatedItemLocal: any = null;
      setCurrentBatch(prev => {
        const items = Array.isArray(prev.consolidatedItems) ? [...prev.consolidatedItems] : [];
        const cur = items[idx];
        const cap = Math.max(1, Number(cur.pickedQuantity || cur.totalQuantity || 0));
        const nextConfirmed = Math.min(Number(cur.confirmedQuantity || 0) + 1, cap);
        const patched = { ...cur, confirmedQuantity: nextConfirmed };
        items[idx] = patched;
        updatedItems = items;
        updatedItemLocal = patched;
        return { ...prev, consolidatedItems: items, updatedAt: new Date() } as any;
      });

      setScanHistory([{ barcode, type: 'product', success: true, data: updatedItemLocal as any, timestamp: new Date() }, ...scanHistory]);
      message.success('確認 +1');

      // 後端持久化（帶上當前運單的訂單號）
      try {
        const identToSave = currentBatch.id || currentBatch.batchPickingNumber;
        if (identToSave && updatedItemLocal) {
          const payload = [{
            orderNumber: currentWaybillOrder,
            sku: String(updatedItemLocal.productSku || updatedItemLocal.productId || ''),
            barcode: updatedItemLocal.productBarcode || null,
            totalQty: Number(updatedItemLocal.totalQuantity || 0),
            pickedQty: Number(updatedItemLocal.pickedQuantity || 0),
            confirmedQty: Number(updatedItemLocal.confirmedQuantity || 0)
          }];
          await apiService.upsertPickingScanItems(String(identToSave), payload);
        }
      } catch {}

      // 全部行達成：自動為該運單建立包裝單
      const allOk = Array.isArray(updatedItems) && updatedItems.length > 0 && updatedItems.every(it => {
        const picked = Number(it.pickedQuantity || it.totalQuantity || 0);
        return Number(it.confirmedQuantity || 0) >= Math.min(picked, Math.max(1, Number(it.totalQuantity || 0)));
      });
      if (allOk && currentWaybillOrder) {
        void handleCreatePackingForSingleOrder(currentWaybillOrder);
      }
      return;
    }
    const resolveSku = () => {
      let hitSku = (bc && bc[normalized]) || allowedBarcodeToSku[normalized] || '';
      if (!hitSku) {
        const dict = sku && Object.keys(sku).length ? sku : allowedSkuToMeta;
        const entry = Object.entries(dict).find(([, meta]) => normalize(meta.barcode) === normalized);
        if (entry) hitSku = entry[0];
      }
      // 允許直接掃描 SKU（normalize 後與白名單鍵相同）
      if (!hitSku) {
        const dict = sku && Object.keys(sku).length ? sku : allowedSkuToMeta;
        if (dict[normalized]) hitSku = normalized;
      }
      if (!hitSku && allowedBarcodeSet.has(normalized)) {
        const entry = Object.entries(bc && Object.keys(bc).length ? bc : allowedBarcodeToSku).find(([bcode]) => bcode === normalized);
        hitSku = entry ? entry[1] : '';
      }
      return hitSku;
    };

    let hitSku = resolveSku();
    const dict = sku && Object.keys(sku).length ? sku : allowedSkuToMeta;
    let meta = hitSku ? dict[hitSku] : undefined;
    // 条码兜底：以条码反查 SKU 并校验白名单
    if ((!hitSku || !meta) && normalized) {
      try {
        const byBc: any = await apiService.getProductByBarcode(normalized).catch(() => null);
        const candidateRaw = String(byBc?.product?.itemCode || byBc?.itemCode || '');
        const candidate = normalize(candidateRaw);
        if (candidate) {
          let ensuredMeta = (dict[candidate] || allowedSkuToMeta[candidate]) as any;
          // 若白名單沒有，動態加入（默認需求=1），使其成為允許掃描的 SKU
          if (!ensuredMeta) {
            const prodName = String(byBc?.product?.itemName || byBc?.itemName || candidateRaw);
            const prodBarcode = normalize(byBc?.product?.barcode || byBc?.barcode || normalized);
            ensuredMeta = { name: prodName, barcode: prodBarcode || null, expectedQty: 1, originalSku: candidateRaw } as any;
            setAllowedSkuToMeta(prev => ({ ...prev, [candidate]: ensuredMeta }));
          }
          hitSku = candidate;
          meta = ensuredMeta;
          setAllowedBarcodeToSku(prev => ({ ...prev, [normalized]: candidate }));
          setAllowedBarcodeSet(prev => new Set(Array.from(prev).concat([normalized])));
        }
      } catch {}
    }
    // 主碼/主SKU 展開：建立子件行（不自動+已拣）
    if ((!hitSku || !meta) && normalized) {
      try {
        let bundleSku = dict[normalized] ? normalized : '';
        if (!bundleSku) {
          const byBc: any = await apiService.getProductByBarcode(normalized).catch(() => null);
          bundleSku = normalize(byBc?.product?.itemCode || byBc?.itemCode || '');
        }
        if (bundleSku) {
          const byBc: any = await apiService.getProductByBarcode(normalized).catch(() => null);
          const rawSkuForQuery = String(byBc?.product?.itemCode || byBc?.itemCode || bundleSku);
          const list: any = await apiService.getProductsV2({ sku: rawSkuForQuery, limit: 1 }).catch(() => null);
          const pid2 = Array.isArray(list?.products) && list.products.length > 0 ? (list.products[0].id || list.products[0].productId) : null;
          const productName = String(list?.products?.[0]?.itemName || list?.products?.[0]?.name || rawSkuForQuery);
          // 讀取套裝子件，用於校驗是否屬於本批次（白名單）
          let intersects = false;
          try {
            if (pid2) {
              const pDetail: any = await apiService.getProductByIdV2(String(pid2)).catch(() => null);
              const children: any[] = Array.isArray((pDetail as any)?.bundleItems) ? (pDetail as any).bundleItems as any[] : [];
              const dictAll = sku && Object.keys(sku).length ? sku : allowedSkuToMeta;
              for (const c of children) {
                const cSku = normalize(c.sku || c.productId || '');
                if (cSku && dictAll[cSku]) { intersects = true; break; }
              }
            }
          } catch {}
          if (!intersects) {
            const scanResult: ScanResult = { 
              barcode, 
              type: 'product', 
              success: false, 
              error: '❌ 產品不屬於此批次\n此條碼不在當前批次的產品清單中\n請確認條碼是否正確', 
              timestamp: new Date() 
            };
            setScanHistory([scanResult, ...scanHistory]);
            playErrorSound();
            message.error('此條碼不屬於該批次的出庫產品');
            return;
          }
          // 若已存在主產品行 -> 直接 +1；否則建立一行並計為 1
          let payloadForSave: any = null;
          let didConfirmBundle = false;
          setCurrentBatch(prev => {
            const items = Array.isArray(prev.consolidatedItems) ? [...prev.consolidatedItems] : [];
            const idx = items.findIndex(i => normalize(i.productSku) === bundleSku);
            if (idx >= 0) {
              const current = items[idx];
              const picked = Number(current.pickedQuantity || 0);
              const total = Number(current.totalQuantity || 0);
              const cap = Math.max(1, total);
              if (cap > 0 && picked >= cap) {
                // 已拣滿 -> 二次掃描視作確認 +1
                const confirmed = Math.min(Number(current.confirmedQuantity || 0) + 1, cap);
                items[idx] = { ...current, totalQuantity: cap, confirmedQuantity: confirmed };
                didConfirmBundle = true;
              } else {
                const nextPicked = Math.min(picked + 1, cap);
                items[idx] = { ...current, pickedQuantity: nextPicked, totalQuantity: cap };
              }
              payloadForSave = items[idx];
              return { ...prev, consolidatedItems: items, updatedAt: new Date() } as any;
            }
            const mapped: any = {
              productId: bundleSku,
              productSku: bundleSku,
              productNameEn: productName,
              productNameCn: productName,
              productBarcode: normalized,
              totalQuantity: 1,
              pickedQuantity: 1,
              confirmedQuantity: 0,
              locationFrom: prev.locationFrom || '主倉庫',
              locationTo: prev.locationTo || '打包區',
              lotNumbers: [],
              serialNumbers: [],
              expirationDates: [],
              sourceOrders: [],
              hasDiscrepancy: false,
              isBundleHeader: true
            };
            payloadForSave = mapped;
            return { ...prev, consolidatedItems: [...items, mapped], updatedAt: new Date() } as any;
          });
          message.success(`已掃描套裝主產品 +1`);
          const scanResult: ScanResult = { barcode, type: 'product', success: true, data: { bundleSku } as any, timestamp: new Date() };
          setScanHistory([scanResult, ...scanHistory]);
          // 立即保存父產品的掃描進度（含確認或已拣）
          try {
            const identToSave = currentBatch.id || currentBatch.batchPickingNumber;
            if (payloadForSave && identToSave) {
              const payload = [{
                orderNumber: (currentBatch as any).pickOrderNumbers?.[0] || null,
                sku: String(payloadForSave.productSku || payloadForSave.productId || ''),
                barcode: payloadForSave.productBarcode || null,
                totalQty: Number(payloadForSave.totalQuantity || 0),
                pickedQty: Number(payloadForSave.pickedQuantity || 0),
                confirmedQty: Number(payloadForSave.confirmedQuantity || 0)
              }];
              await apiService.upsertPickingScanItems(String(identToSave), payload);
            }
          } catch {}
          return; // 完成
        }
      } catch {}
    }
    console.log('[SCAN] resolve sku:', hitSku, 'meta?', !!meta);
    if (!hitSku || !meta) {
      const scanResult: ScanResult = { 
        barcode, 
        type: 'product', 
        success: false, 
        error: '❌ 無法識別此條碼\n此條碼不在產品清單中\n請檢查條碼是否正確或聯繫管理員', 
        timestamp: new Date() 
      };
      setScanHistory([scanResult, ...scanHistory]);
      playErrorSound();
      message.error('此條碼不屬於該批次的出庫產品');
      return;
    }

    // 去除「已完成掃描」的提示，改為允許進入並把確認數量 +1 的流程

    // 一次性函數式更新：若不存在則補建，然後已拣+1
    let updatedItem: ConsolidatedPickingItem | null = null;
    setCurrentBatch(prev => {
      const items = Array.isArray(prev.consolidatedItems) ? prev.consolidatedItems : [];
      const idx = items.findIndex(i => i.productSku === hitSku || normalize(i.productBarcode) === normalized);
      console.log('[SCAN] before update: idx=', idx, 'itemsLen=', items.length);
      let nextItems: ConsolidatedPickingItem[];
      if (idx < 0) {
        const mapped: ConsolidatedPickingItem = {
          productId: hitSku || normalized,
          productSku: hitSku || normalized,
          productNameEn: meta.name,
          productNameCn: meta.name,
          productBarcode: meta.barcode || barcode,
          totalQuantity: meta.expectedQty || 0,
          pickedQuantity: 0,
          confirmedQuantity: 0,
          locationFrom: prev.locationFrom || '主倉庫',
          locationTo: prev.locationTo || '打包區',
          lotNumbers: [],
          serialNumbers: [],
          expirationDates: [],
          sourceOrders: [],
          hasDiscrepancy: false
        };
        const cap = Math.max(1, Number(mapped.totalQuantity || 0));
        updatedItem = { ...mapped, pickedQuantity: Math.min(1, cap), totalQuantity: cap };
        nextItems = [...items, updatedItem];
        console.log('[SCAN] appended new item for sku=', hitSku);
      } else {
        const total = Number(items[idx].totalQuantity || 0);
        const cap = Math.max(1, total);
        const picked = Number(items[idx].pickedQuantity || 0);
        if (cap > 0 && picked >= cap) {
          // 已拣滿 -> 視為確認 +1
          const confirmed = Math.min(Number(items[idx].confirmedQuantity || 0) + 1, cap);
          updatedItem = { ...items[idx], totalQuantity: cap, confirmedQuantity: confirmed };
        } else {
          const inc = Math.min(picked + 1, cap);
          updatedItem = { ...items[idx], pickedQuantity: inc, totalQuantity: cap };
        }
        nextItems = [...items];
        nextItems[idx] = updatedItem;
        console.log('[SCAN] increased (picked/confirmed) for sku=', hitSku);
      }
      const nb = { ...prev, consolidatedItems: nextItems, updatedAt: new Date() } as BatchPickingList;
      console.log('[SCAN] after update: listSize=', nextItems.length);
      return nb;
    });

    // 記錄掃描歷史與高亮
    setTimeout(async () => {
      if (updatedItem) {
        setScanHistory([{ barcode, type: 'product', success: true, data: updatedItem as any, timestamp: new Date() }, ...scanHistory]);
        setLastScannedId((updatedItem as any).productId);
        message.success(`${(updatedItem as any).productNameCn} 揀貨 +1`);

        // 保存到後端（debounce 由後端承受即可；此處單次上傳當次變更）
        try {
          const ident = currentBatch.id || currentBatch.batchPickingNumber;
          const payload = [{
            orderNumber: (currentBatch as any).pickOrderNumbers?.[0] || null,
            sku: String((updatedItem as any).productSku || (updatedItem as any).productId || ''),
            barcode: (updatedItem as any).productBarcode || null,
            totalQty: Number((updatedItem as any).totalQuantity || 0),
            pickedQty: Number((updatedItem as any).pickedQuantity || 0),
            confirmedQty: Number((updatedItem as any).confirmedQuantity || 0)
          }];
          if (ident) await apiService.upsertPickingScanItems(String(ident), payload);
        } catch {}
      }
    }, 0);
  };

  const handleVerificationScan = async (barcode: string) => {
    const { bc, sku } = await ensureWhiteListReady();
    console.log('currentBatch=======================:', currentBatch.consolidatedItems.length)
    const normalized = normalize(barcode);
    // Find the product for verification (normalize compare)
    let item = currentBatch.consolidatedItems.find(item => 
      normalize(item.productBarcode) === normalized || 
      normalize(item.productSku) === normalized ||
      item.lotNumbers.includes(barcode) ||
      item.serialNumbers.includes(barcode)
    );
    // 後續更新以 baseItems 為準，避免使用舊快照覆蓋補建結果
    let baseItems = currentBatch.consolidatedItems;

    // 若核對時尚未在列表中，且條碼確屬於本批次，補建一行
    if (!item) {
      let hitSku = (bc && bc[normalized]) || allowedBarcodeToSku[normalized] || '';
      if (!hitSku) {
        const dict0 = sku && Object.keys(sku).length ? sku : allowedSkuToMeta;
        const e0 = Object.entries(dict0).find(([, meta]) => normalize(meta.barcode) === normalized);
        if (e0) hitSku = e0[0];
      }
      console.log('[VERIFY] resolve sku:', hitSku || '(none)', 'normalized=', normalized);
      let newItem: ConsolidatedPickingItem | null = null;
      // 命中白名單：按白名單信息補建
      if (hitSku && ((sku && sku[hitSku]) || allowedSkuToMeta[hitSku])) {
        const dict1 = sku && Object.keys(sku).length ? sku : allowedSkuToMeta;
        const meta = dict1[hitSku];
        newItem = {
          productId: hitSku,
          productSku: hitSku,
          productNameEn: meta.name,
          productNameCn: meta.name,
          productBarcode: meta.barcode || normalized,
          totalQuantity: Math.max(1, meta.expectedQty || 0),
          pickedQuantity: Math.max(1, meta.expectedQty || 1),
          confirmedQuantity: 0,
          locationFrom: currentBatch.locationFrom || '主倉庫',
          locationTo: currentBatch.locationTo || '打包區',
          lotNumbers: [],
          serialNumbers: [],
          expirationDates: [],
          sourceOrders: [],
          hasDiscrepancy: false
        };
      } else {
        // 仍未命中：按條碼強制補建一行（最小信息），避免列表缺失
        newItem = {
          productId: normalized,
          productSku: normalized,
          productNameEn: '掃描產品',
          productNameCn: '掃描產品',
          productBarcode: normalized,
          totalQuantity: 1,
          pickedQuantity: 1,
          confirmedQuantity: 0,
          locationFrom: currentBatch.locationFrom || '主倉庫',
          locationTo: currentBatch.locationTo || '打包區',
          lotNumbers: [],
          serialNumbers: [],
          expirationDates: [],
          sourceOrders: [],
          hasDiscrepancy: false
        };
      }
      const patched = { ...currentBatch, consolidatedItems: [...(currentBatch.consolidatedItems || []), newItem], updatedAt: new Date() } as BatchPickingList;
      console.log('[VERIFY] appended new item. newSize=', patched.consolidatedItems.length);
      setCurrentBatch(patched); // 僅本地更新
      baseItems = patched.consolidatedItems; // 之後的計算基於最新列表
      setLastScannedId(newItem.productId);
      setScanHistory([{ barcode, type: 'product', success: true, data: newItem as any, timestamp: new Date() }, ...scanHistory]);
      item = newItem;
    }

    if (!item) {
      playErrorSound();
      message.error('未找到對應產品');
      return;
    }

    // Check if there's a discrepancy
    if (item.confirmedQuantity >= item.pickedQuantity) {
      playErrorSound();
      message.warning('該產品已確認完畢');
      return;
    }

    // Auto-increment confirmed quantity by 1
    const updatedItems = baseItems.map(i => {
      if (i.productId === item.productId) {
        const newConfirmedQty = Math.min(i.confirmedQuantity + 1, i.pickedQuantity);
        
        // Check for discrepancy
        if (newConfirmedQty !== i.totalQuantity) {
          return {
            ...i,
            confirmedQuantity: newConfirmedQty,
            hasDiscrepancy: true,
            discrepancyReason: newConfirmedQty < i.totalQuantity ? '數量不足' : '數量超出'
          };
        }
        
        return {
          ...i,
          confirmedQuantity: newConfirmedQty,
          hasDiscrepancy: false
        };
      }
      return i;
    });

    const updatedBatch = {
      ...currentBatch,
      consolidatedItems: updatedItems,
      updatedAt: new Date()
    };

    setCurrentBatch(updatedBatch); // 僅本地更新，避免父組件覆蓋
    
    const scanResult: ScanResult = {
      barcode,
      type: 'product',
      success: true,
      data: item,
      timestamp: new Date()
    };
    setScanHistory([scanResult, ...scanHistory]);
    
    message.success(`${item.productNameCn} 確認 +1`);
    
    // Check if there are discrepancies
    const itemWithDiscrepancy = updatedItems.find(i => i.hasDiscrepancy);
    if (itemWithDiscrepancy && itemWithDiscrepancy.confirmedQuantity === itemWithDiscrepancy.pickedQuantity) {
      setCurrentDiscrepancy({
        batchPickingId: currentBatch.id,
        productId: itemWithDiscrepancy.productId,
        productSku: itemWithDiscrepancy.productSku,
        productName: itemWithDiscrepancy.productNameCn,
        expectedQuantity: itemWithDiscrepancy.totalQuantity,
        actualQuantity: itemWithDiscrepancy.confirmedQuantity,
        discrepancyType: itemWithDiscrepancy.confirmedQuantity < itemWithDiscrepancy.totalQuantity ? 'shortage' : 'overage'
      });
      setDiscrepancyModalVisible(true);
    }
    
    // Check if all items are verified
    const allVerified = updatedItems.every(item =>
      item.confirmedQuantity === item.pickedQuantity && !item.hasDiscrepancy
    );
    if (allVerified) {
      Modal.success({
        title: '核對完成',
        content: '所有產品核對完成，已設為待攬收',
      });
      const finalBatch = {
        ...updatedBatch,
        status: 'completed' as const,
        updatedAt: new Date()
      };
      setCurrentBatch(finalBatch);
      onUpdateBatchPicking(finalBatch);

      // 自動重置到乾淨的揀貨頁面（延遲執行以便用戶看到完成訊息）
      setTimeout(() => {
        // 重置所有狀態到初始值
        setScanHistory([]);
        setCurrentBatch(batchPickingList);
        setScanMode('picking');
        setLastScannedId(null);
        setAllScanned(false);
        setCurrentWaybillOrder(null);
        setCurrentWaybillNumber(null);
        setWaybillOrderProducts([]);
        setDiscrepancyModalVisible(false);
        setCurrentDiscrepancy(null);
        setScanBuffer('');
        setLastScanTime(0);
        setManualScanValue('');
        didMarkAllocatingRef.current = false;
        didMarkVerifyingRef.current = false;

        // 通知父組件
        onUpdateBatchPicking(batchPickingList);

        message.info('已重置揀貨頁面，可以開始新的揀貨', 3);
      }, 1500);
    }
  };

  // 运单扫描处理函数
  const handleWaybillScan = async (barcode: string) => {
    try {
      // 新流程：直接用運單號查詢出庫產品（不依賴批次）
      const resp: any = await apiService.getPickingItemsByWaybill(String(barcode).trim());
      const data = (resp && (resp.data ?? resp)) || {};
      const items: any[] = Array.isArray(data.items) ? data.items : [];
      const orders: any[] = Array.isArray(data.orders) ? data.orders : [];
      if (!items.length) {
        playErrorSound();
        message.error('未找到此運單的任何產品');
        setScanHistory([{ barcode, type: 'batch_picking', success: false, error: '❌ 未找到此運單的產品', timestamp: new Date() }, ...scanHistory]);
        return false;
      }

      // 構建白名單與清單（已拣=需求）
      const bcToSku: Record<string, string> = {};
      const skuToMeta: Record<string, { name: string; barcode: string | null; expectedQty: number; originalSku?: string }> = {};
      const consolidated: ConsolidatedPickingItem[] = items.map((it: any) => {
        const rawSku = String(it.sku || '');
        const sku = normalize(rawSku);
        const bc = normalize(it.productBarcode || '');
        const qty = Math.max(1, Number(it.quantity || 1));
        const name = String(it.productName || rawSku);
        if (sku) skuToMeta[sku] = { name, barcode: bc || null, expectedQty: qty, originalSku: rawSku };
        if (bc) bcToSku[bc] = sku;
        return {
          productId: sku || rawSku,
          productSku: sku || rawSku,
          productNameEn: name,
          productNameCn: name,
          productBarcode: bc || '',
          totalQuantity: qty,
          pickedQuantity: qty,
          confirmedQuantity: 0,
          locationFrom: currentBatch.locationFrom || '主倉庫',
          locationTo: currentBatch.locationTo || '打包區',
          lotNumbers: [],
          serialNumbers: [],
          expirationDates: [],
          sourceOrders: [],
          hasDiscrepancy: false
        } as ConsolidatedPickingItem;
      });

      setAllowedBarcodeToSku(bcToSku);
      setAllowedSkuToMeta(skuToMeta);
      setAllowedBarcodeSet(new Set(Object.keys(bcToSku)));
      setWaybillOrderProducts(consolidated as any);
      setCurrentBatch(prev => ({ ...prev, consolidatedItems: consolidated, updatedAt: new Date() } as any));

      const orderLabel = orders.length ? String(orders[0].orderNumber || '') : String(data.waybillNumber || barcode);
      setCurrentWaybillOrder(orderLabel);
      setCurrentWaybillNumber(String(data.waybillNumber || barcode));
      message.success(`已載入運單產品列表：${consolidated.length} 項`);
      setScanHistory([{ barcode, type: 'batch_picking', success: true, data: { orderNumber: orderLabel, productCount: consolidated.length }, timestamp: new Date() }, ...scanHistory]);
      // 運單產品已載入 → 立即將關聯出庫單狀態標記為「配貨中」
      try {
        const ids = orders.map(o => String(o.id || '')).filter(Boolean);
        const numbers = orders.map(o => String(o.orderNumber || o.outbound_number || '')).filter(Boolean);
        await ensureOutboundAllocatingOnce({ orderIds: ids, orderNumbers: numbers.length ? numbers : (orderLabel ? [orderLabel] : []) });
      } catch {}
      return true;
    } catch (error: any) {
      const errorMsg = error?.message || error?.response?.data?.message || '系統錯誤';
      playErrorSound();
      message.error('掃描運單失敗');
      setScanHistory([{ 
        barcode, 
        type: 'batch_picking', 
        success: false, 
        error: `❌ 掃描運單失敗\n錯誤原因：${errorMsg}\n請重試或聯繫管理員`, 
        timestamp: new Date() 
      }, ...scanHistory]);
      return false;
    }
  };

  const handleScan = async (barcode: string) => {
    if (!barcode || !barcode.trim()) return;
    
    setScanning(true);
    
    try {
      // 新逻辑：首次掃描必須作為運單；若清單仍為空或尚未設定運單，優先嘗試運單掃描
      const hasItems = Array.isArray(currentBatch.consolidatedItems) && currentBatch.consolidatedItems.length > 0;
      if (!currentWaybillOrder && !hasItems) {
        const isWaybill = await handleWaybillScan(barcode.trim());
        if (!isWaybill) {
          // 未識別為運單時，不把首筆掃描當作產品，避免誤打 products lookup
          playErrorSound();
          message.error('請先掃描運單條碼');
        }
        return;
      }
      
      // 已有运单或不是运单时，作为产品扫描
      switch (scanMode) {
        case 'picking':
          await handleProductScan(barcode.trim());
          break;
        case 'verification':
          await handleVerificationScan(barcode.trim());
          break;
      }
    } finally {
      setScanning(false);
    }
  };

  // 为单个订单创建包装（运单扫描流程使用）
  const handleCreatePackingForSingleOrder = async (orderNumber: string) => {
    try {
      // 直接用當前畫面產品列表構造包裝明細，避免依賴批次接口
      const items = (currentBatch.consolidatedItems || []).map((ci: any) => ({
        productSku: String(ci.productSku || ''),
        productName: String(ci.productNameCn || ci.productNameEn || ''),
        productBarcode: String(ci.productBarcode || ''),
        expectedQuantity: Number(ci.totalQuantity || 0),
        scannedQuantity: 0,
        packedQuantity: 0,
        lotNumber: '',
        serialNumber: '',
        status: 'pending'
      }));
      if (!items.length) {
        playErrorSound();
        message.error('找不到可用的產品明細');
        return null;
      }

      const packPayload = {
        orderNumber: orderNumber,
        waybillNumber: currentWaybillNumber || currentBatch.batchPickingBarcode || '',
        outboundOrderId: null,
        status: 'pending',
        items
      };

      const baseUrl = `${(apiService as any).baseURL || ''}`;
      const token = localStorage.getItem('authToken');
      
      const resp = await fetch(`${baseUrl}/packing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(packPayload)
      });

      if (!resp.ok) {
        throw new Error(`創建包裝失敗: ${resp.status}`);
      }
      
      const packingResult = await resp.json();
      const packingId = packingResult.id || packingResult.data?.id;
      
      // 可選：更新對應出庫單狀態為已打包（此流程以運單為主，無出庫ID時跳過）

      message.success(`訂單 ${orderNumber} 已完成拣貨並移至包裝管理`);

      // 刷新批次状态
      onUpdateBatchPicking({ ...currentBatch, updatedAt: new Date() } as any);

      // 自動重置到乾淨的揀貨頁面（延遲執行以便用戶看到完成訊息）
      setTimeout(() => {
        // 重置所有狀態到初始值
        setScanHistory([]);
        setCurrentBatch(batchPickingList);
        setScanMode('picking');
        setLastScannedId(null);
        setAllScanned(false);
        setCurrentWaybillOrder(null);
        setCurrentWaybillNumber(null);
        setWaybillOrderProducts([]);
        setDiscrepancyModalVisible(false);
        setCurrentDiscrepancy(null);
        setScanBuffer('');
        setLastScanTime(0);
        setManualScanValue('');
        didMarkAllocatingRef.current = false;
        didMarkVerifyingRef.current = false;

        // 通知父組件
        onUpdateBatchPicking(batchPickingList);

        message.info('已重置揀貨頁面，可以開始新的揀貨', 3);
      }, 1500);

      // 返回packing订单信息
      return {
        packingId,
        orderNumber,
        packingOrder: packingResult.data || packingResult
      };
      
    } catch (error) {
      console.error('創建包裝失敗:', error);
      playErrorSound();
      message.error('自動完成包裝失敗，請重試');
      return null;
    }
  };

  const handleCreatePacking = async () => {
    try {
      // 構造多訂單包裝：按該批次的出庫訂單逐一建立
      const ident = currentBatch.id || currentBatch.batchPickingNumber;
      // 若沒有批次識別，且存在運單上下文，直接以運單模式建單
      if (!ident && currentWaybillOrder) {
        await handleCreatePackingForSingleOrder(currentWaybillOrder);
        return;
      }
      const [ordersResp, printDataResp]: any = await Promise.all([
        apiService.getPickingOrders(String(ident)) as any,
        apiService.getPickingPrintData(String(ident)) as any
      ]);

      const orders: Array<any> = (ordersResp && (ordersResp.data ?? ordersResp)) || [];
      const pdItems: Array<any> = (printDataResp && ((printDataResp.data && printDataResp.data.items) ?? printDataResp.items)) || [];

      // 建立 orderId -> items 的映射（兼容多種欄位：orderId/order_id/outboundOrderId/outbound_order_id/orderNumber/outbound_number 等）
      const numberToId = new Map<string, string>();
      for (const o of orders) {
        const num = String(o.orderNumber || o.outbound_number || '');
        if (num) numberToId.set(num, String(o.id || ''));
      }
      const orderIdToItems = new Map<string, Array<any>>();
      const unassignedItems: Array<any> = [];
      for (const it of pdItems) {
        const candidateId = String(
          it.orderId || it.order_id || it.outboundOrderId || it.outbound_order_id || ''
        );
        const candidateNum = String(it.orderNumber || it.outbound_number || it.orderNo || it.soNumber || '');
        const oid = candidateId || (numberToId.get(candidateNum) || '');
        if (!oid) { unassignedItems.push(it); continue; }
        if (!orderIdToItems.has(oid)) orderIdToItems.set(oid, []);
        orderIdToItems.get(oid)!.push(it);
      }

      // 若 print-data 未提供訂單分配，使用穩定輪詢方式把未分配 SKU 分攤到各訂單，避免重覆落入每個訂單
      if (unassignedItems.length > 0 && orders.length > 0) {
        const orderIds = orders.map(o => String(o.id || '')).filter(Boolean).sort();
        const uniqueSkuList: string[] = Array.from(new Set(unassignedItems.map(x => normalize(x.sku || x.productSku || '')))).filter(Boolean);
        const skuToOrderId = new Map<string, string>();
        uniqueSkuList.forEach((sku, idx) => {
          const orderId = orderIds[idx % orderIds.length];
          skuToOrderId.set(sku, orderId);
        });
        for (const it of unassignedItems) {
          const skuNorm = normalize(it.sku || it.productSku || '');
          const oid = skuToOrderId.get(skuNorm);
          if (!oid) continue;
          if (!orderIdToItems.has(oid)) orderIdToItems.set(oid, []);
          orderIdToItems.get(oid)!.push(it);
        }
      }

      // 當前批次已掃描數（按 SKU 匯總）
      const skuToScanned = new Map<string, number>();
      for (const i of currentBatch.consolidatedItems || []) {
        skuToScanned.set(i.productSku, Number(i.pickedQuantity || 0));
      }

      const baseUrl = `${(apiService as any).baseURL || ''}`;
      const token = localStorage.getItem('authToken');
      let createdCount = 0;
      for (const order of orders) {
        const orderId = String(order.id || '');
        const orderNumber = order.orderNumber || order.outbound_number || orderId;
        let rawItems = orderIdToItems.get(orderId) || [];

        // 僅當只有單一訂單時才使用 consolidatedItems 作為後備，避免在多訂單情況下把全部 SKU 重覆寫入每個訂單
        const shouldUseFallbackAll = rawItems.length === 0 && orders.length === 1;
        let items = ((rawItems.length > 0)
          ? rawItems.map((it: any) => {
              const rawSku = String(it.sku || it.productSku || '');
              const normalizedSku = normalize(rawSku);
              // 期望數量為 0 時，回退使用 consolidatedItems 中對應 SKU 的 totalQuantity
              let expectedQty = Number(it.quantity || 0);
              if (!expectedQty) {
                const match = (currentBatch.consolidatedItems || []).find(ci => normalize(ci.productSku) === normalizedSku);
                expectedQty = Number(match?.totalQuantity || 0);
              }
              const scannedQty = Math.min(expectedQty || 0, skuToScanned.get(normalizedSku) || 0);
              // 扣減已分配的掃描量，避免重複分攤
              skuToScanned.set(normalizedSku, Math.max(0, (skuToScanned.get(normalizedSku) || 0) - scannedQty));
              // 優先輸出後端原始 SKU（含下劃線等），避免被 normalize 影響
              return {
                productSku: rawSku,
                productName: String(it.product || it.productName || rawSku),
                productBarcode: String(it.productBarcode || ''),
                expectedQuantity: expectedQty || 0,
                scannedQuantity: 0,
                packedQuantity: 0,
                lotNumber: '',
                serialNumber: '',
                status: 'pending'
              };
            })
          : shouldUseFallbackAll ? (currentBatch.consolidatedItems || []).map((ci: any) => {
              const normSku = normalize(ci.productSku);
              const meta = allowedSkuToMeta[normSku];
              const outputSku = (meta && meta.originalSku) ? meta.originalSku : String(ci.productSku || '');
              return {
                productSku: outputSku,
              productName: String(ci.productNameCn || ci.productNameEn || ''),
              productBarcode: String(ci.productBarcode || ''),
              expectedQuantity: Number(ci.totalQuantity || 0),
              scannedQuantity: 0,
              packedQuantity: 0,
              lotNumber: '',
              serialNumber: '',
              // 將是否為套裝主產品標記同步到打包明細，便於核對時跳過子產品
              isBundleHeader: ci.isBundleHeader === true,
              status: 'pending'
              };
            }) : []
        );

        // 單一訂單且 print-data 可能缺少部分 SKU：用當前批次的匯總列表補齊缺失項，避免只保存部分產品
        if (orders.length === 1) {
          const present = new Set<string>(items.map((x: any) => normalize(x.productSku || '')));
          for (const ci of (currentBatch.consolidatedItems || [])) {
            const skuRaw = String(ci.productSku || '');
            const key = normalize(skuRaw);
            if (!key) continue;
            if (present.has(key)) continue;
            const meta = allowedSkuToMeta[key as keyof typeof allowedSkuToMeta] as any;
            const outputSku = (meta && meta.originalSku) ? meta.originalSku : skuRaw;
            items.push({
              productSku: outputSku,
              productName: String(ci.productNameCn || ci.productNameEn || ''),
              productBarcode: String(ci.productBarcode || ''),
              expectedQuantity: Number(ci.totalQuantity || 0),
              scannedQuantity: 0,
              packedQuantity: 0,
              lotNumber: '',
              serialNumber: '',
              status: 'pending'
            });
            present.add(key);
          }
        }

        const payload = {
          orderNumber,
          waybillNumber: manualScanValue || currentBatch.batchPickingBarcode,
          outboundOrderId: orderId || null,
          status: 'pending',
          items
        };

        const resp = await fetch(`${baseUrl}/packing`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify(payload)
        });
        if (resp.ok) {
          createdCount++;
        } else {
          // 單筆失敗只記錄，不刷屏多條提示
          // 可在必要時將錯誤寫入掃描歷史
        }
      }

      message.success(`已為 ${createdCount} 個銷售訂單建立包裝單`);
      // 導向到打包管理查看新建的包裝單
      navigate('/inventory/packing');
    } catch (err: any) {
      console.error(err);
      playErrorSound();
      message.error(err?.message || '建立包裝單失敗');
    }
  };

  // 手動調整：允許在 UI 中直接輸入已拣/確認數量並做邊界校驗
  const handleManualQuantityUpdate = (productId: string, type: 'picked' | 'confirmed', newQuantity: number) => {
    const updatedItems = (currentBatch.consolidatedItems || []).map(item => {
      if (item.productId !== productId) return item;
      const safe = Number.isFinite(newQuantity) ? Math.max(0, Math.floor(newQuantity)) : 0;
      if (type === 'picked') {
        const bounded = Math.min(safe, Number(item.totalQuantity || 0));
        const confirmed = Math.min(Number(item.confirmedQuantity || 0), bounded);
        return { ...item, pickedQuantity: bounded, confirmedQuantity: confirmed };
      } else {
        // confirmed 不可大於目前 picked
        const bounded = Math.min(safe, Math.max(0, Number(item.pickedQuantity || 0)));
        return { ...item, confirmedQuantity: bounded };
      }
    });

    const updatedBatch = { ...currentBatch, consolidatedItems: updatedItems, updatedAt: new Date() };
    setCurrentBatch(updatedBatch);
    onUpdateBatchPicking(updatedBatch);

    // 保存當前調整到後端
    try {
      const changed = updatedItems.find(it => it.productId === productId);
      const ident = currentBatch.id || currentBatch.batchPickingNumber;
      if (changed && ident) {
        const payload = [{
          orderNumber: (currentBatch as any).pickOrderNumbers?.[0] || null,
          sku: String(changed.productSku || changed.productId || ''),
          barcode: changed.productBarcode || null,
          totalQty: Number(changed.totalQuantity || 0),
          pickedQty: Number(changed.pickedQuantity || 0),
          confirmedQty: Number(changed.confirmedQuantity || 0)
        }];
        void apiService.upsertPickingScanItems(String(ident), payload);
      }
    } catch {}
  };

  const handleResolveDiscrepancy = async (values: any) => {
    if (!currentDiscrepancy) return;
    
    // Create discrepancy record
    const discrepancy: PickingDiscrepancy = {
      id: `DISC-${Date.now()}`,
      batchPickingId: currentBatch.id,
      pickOrderNumber: currentBatch.pickOrderNumbers[0], // Simplified
      productId: currentDiscrepancy.productId!,
      productSku: currentDiscrepancy.productSku!,
      productName: currentDiscrepancy.productName!,
      expectedQuantity: currentDiscrepancy.expectedQuantity!,
      actualQuantity: currentDiscrepancy.actualQuantity!,
      discrepancyType: currentDiscrepancy.discrepancyType!,
      reason: values.reason,
      resolution: values.resolution,
      resolvedAt: new Date(),
      resolvedBy: 'Current User', // Would be actual user
      createdAt: new Date()
    };

    const updatedBatch = {
      ...currentBatch,
      discrepancies: [...currentBatch.discrepancies, discrepancy],
      updatedAt: new Date()
    };

    setCurrentBatch(updatedBatch);
    onUpdateBatchPicking(updatedBatch);
    setDiscrepancyModalVisible(false);
    setCurrentDiscrepancy(null);
    form.resetFields();
    
    message.success('差異已記錄並處理');
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'processing': 'blue',
      'picking': 'purple',
      'checking': 'orange',
      'completed': 'green'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      'processing': '處理中',
      'picking': '配貨中',
      'checking': '核對中',
      'completed': '已完成'
    };
    return texts[status] || status;
  };

  const getCurrentStep = () => {
    switch (scanMode) {
      case 'picking': return 0;
      case 'verification': return 1;
      default: return 0;
    }
  };

  // 使用本地 state 渲染，避免父組件舊 props 覆蓋剛補建的列表
  const localItemsRaw = Array.isArray(currentBatch.consolidatedItems) ? currentBatch.consolidatedItems : [];
  // 去重：以 productSku 優先，退而以 barcode 去重
  const seen = new Set<string>();
  const localItems = localItemsRaw.filter(it => {
    const key = (it.productSku && String(it.productSku)) || (it.productBarcode && normalize(it.productBarcode)) || String(Math.random());
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const totalQuantity = localItems.reduce((sum, item) => sum + Number(item.totalQuantity || 0), 0);
  const pickedQuantity = localItems.reduce((sum, item) => sum + Number(item.pickedQuantity || 0), 0);
  const confirmedQuantity = localItems.reduce((sum, item) => sum + Number(item.confirmedQuantity || 0), 0);
  const discrepancyCount = localItems.filter(item => item.hasDiscrepancy).length;
  // 以白名單逐 SKU/Barcode 校驗是否已拣滿；若白名單尚未就緒則退回到列表法
  const isAllScanned = (() => {
    try {
      console.log('[SCAN][CHECK] localItems size=', Array.isArray(localItems) ? localItems.length : 'n/a', localItems);
      // 先計算列表法判斷：所有行 pickedQuantity >= totalQuantity (>0)
      // 列表法（全部項目）：完全依據當前畫面數據判斷
      const listNeedAll = Array.isArray(localItems)
        ? localItems.filter(it => Number(it.totalQuantity || 0) > 0)
        : [];
      const listOkAll = listNeedAll.length > 0 && listNeedAll.every(it => Number(it.pickedQuantity || 0) >= Number(it.totalQuantity || 0));

      // 列表法（限制白名單）：僅當白名單存在時，才限定在白名單內的 SKU
      const whiteSkuSet = new Set<string>(Object.keys(allowedSkuToMeta || {}).map(normalize));
      const listNeed = Array.isArray(localItems)
        ? localItems.filter(it => whiteSkuSet.has(normalize(it.productSku)) && Number(it.totalQuantity || 0) > 0)
        : [];
      const listOk = listNeed.length > 0 && listNeed.every(it => Number(it.pickedQuantity || 0) >= Number(it.totalQuantity || 0));

      // 沒有任何列表行 → 必然未拣滿（避免空列表錯判已拣滿）
      if (!Array.isArray(localItems) || localItems.length === 0) {
        console.warn('[SCAN][CHECK] result=false (no items)');
        return false;
      }

      const expectedEntries = Object.entries(allowedSkuToMeta || {});
      console.log('[SCAN][CHECK] whitelist size=', expectedEntries.length, allowedSkuToMeta);

      // 若白名單尚未建立：回退為列表法（依畫面行數量判斷）
      if (expectedEntries.length === 0) {
        console.warn('[SCAN][CHECK] whitelist not ready -> fallback to listOkAll=', listOkAll);
        return listOkAll;
      }

      // 將白名單期望數量與當前列表需求合併（取最大值）
      // 目的：避免白名單某 SKU 帶 0 導致該 SKU 被忽略，出現僅掃部分也被判定拣滿的誤判
      const expectedBySku = new Map<string, number>();

      // 1) 先放入白名單的期望數量（可能為 0）
      for (const [rawSku, meta] of expectedEntries) {
        const skuNorm = normalize(rawSku);
        expectedBySku.set(skuNorm, Number((meta as any)?.expectedQty || 0));
      }

      // 2) 以列表上的需求數量補齊/覆蓋（取最大值）
      for (const it of localItems) {
        const s = it.productSku ? normalize(String(it.productSku)) : '';
        if (!s) continue;
        // 多來源兼容：totalQuantity > expectedQuantity
        let qList = Number((it as any).totalQuantity ?? 0);
        if (!qList || qList <= 0) qList = Number((it as any).expectedQuantity ?? 0);
        if (qList && qList > 0) {
          const prev = expectedBySku.get(s) || 0;
          expectedBySku.set(s, Math.max(prev, qList));
        }
      }

      console.log('[SCAN][CHECK] expectedBySku=', Object.fromEntries(expectedBySku));
      const totalExpected = Array.from(expectedBySku.values()).reduce((a, b) => a + b, 0);
      if (totalExpected <= 0) {
        console.warn('[SCAN][CHECK] result=false (no expected after fallback)');
        return false;
      }

      // 彙總當前已拣：分別按 SKU 與 Barcode 兩個索引聚合，避免漏計
      const pickedBySku = new Map<string, number>();
      const pickedByBc = new Map<string, number>();
      for (const it of localItems) {
        const s = it.productSku ? normalize(String(it.productSku)) : '';
        const b = it.productBarcode ? normalize(String(it.productBarcode)) : '';
        const p = Number(it.pickedQuantity || 0);
        if (s) pickedBySku.set(s, (pickedBySku.get(s) || 0) + p);
        if (b) pickedByBc.set(b, (pickedByBc.get(b) || 0) + p);
      }
      console.log('[SCAN][CHECK] pickedBySku=', Object.fromEntries(pickedBySku));
      console.log('[SCAN][CHECK] pickedByBarcode=', Object.fromEntries(pickedByBc));

      // 對白名單逐 SKU 校驗（不提早返回，計算出 whitelistOk）
      let whitelistOk = true;
      for (const [rawSku, meta] of expectedEntries) {
        const skuNorm = normalize(rawSku);
        const expectedQty = expectedBySku.get(skuNorm) || Number((meta as any)?.expectedQty || 0);
        if (expectedQty <= 0) continue; // 不需拣
        const bcNorm = (meta as any)?.barcode ? normalize((meta as any).barcode) : '';
        const pickedSku = pickedBySku.get(skuNorm) || 0;
        const pickedBc = bcNorm ? (pickedByBc.get(bcNorm) || 0) : 0;
        const picked = Math.max(pickedSku, pickedBc);
        console.log('[SCAN][CHECK] sku=', skuNorm, 'expected=', expectedQty, 'pickedSku=', pickedSku, 'pickedBc=', pickedBc, 'picked=', picked);
        if (picked < expectedQty) {
          whitelistOk = false;
          break;
        }
      }
      console.log('[SCAN][CHECK] result (whitelistOk=', whitelistOk, ', listOk=', listOk, ')');
      // 白名單已就緒時，以白名單為唯一依據
      return whitelistOk;
    } catch (e) {
      console.error('[SCAN][CHECK] error while computing isAllScanned:', e);
      return false;
    }
  })();

  // 二次確認：所有產品 confirmedQuantity >= totalQuantity (>0)
  const isAllConfirmed = (() => {
    const items = localItems;
    if (!Array.isArray(items) || items.length === 0) return false;
    return items.every(i => Number(i.totalQuantity || 0) > 0 && Number(i.confirmedQuantity || 0) >= Number(i.totalQuantity || 0));
  })();

  // 以「確認值 === 已拣值（且無差異）」作為核對完成的條件，觸發狀態改為 VERIFYING
  const isAllConfirmedEqPicked = (() => {
    const items = localItems;
    if (!Array.isArray(items) || items.length === 0) return false;
    return items.every(i => Number(i.pickedQuantity || 0) > 0 && Number(i.confirmedQuantity || 0) === Number(i.pickedQuantity || 0) && !i.hasDiscrepancy);
  })();

  const uiDrivenVerifyingRef = useRef<boolean>(false);
  useEffect(() => {
    if (isAllConfirmedEqPicked && !uiDrivenVerifyingRef.current) {
      uiDrivenVerifyingRef.current = true;
      // 若處於運單掃描模式，優先以單號直接更新狀態為 VERIFYING
      if (currentWaybillOrder) {
        void ensureOutboundVerifyingOnce({ orderNumbers: [currentWaybillOrder] });
      } else {
        void ensureOutboundVerifyingOnce();
      }
    }
    if (!isAllConfirmedEqPicked) {
      // 回退時允許再次觸發
      uiDrivenVerifyingRef.current = false;
    }
  }, [isAllConfirmedEqPicked, currentWaybillOrder]);

  // 自動完成包裝：當所有產品確認完成後，自動觸發包裝流程
  const autoPackingTriggeredRef = useRef<boolean>(false);
  useEffect(() => {
    if (isAllConfirmed && !autoPackingTriggeredRef.current) {
      autoPackingTriggeredRef.current = true;
      // 延遲執行，確保UI更新完成
      setTimeout(() => {
        if (currentWaybillOrder) {
          void handleCreatePackingForSingleOrder(currentWaybillOrder);
        } else {
          void handleCreatePacking();
        }
      }, 500);
    }
    if (!isAllConfirmed) {
      // 回退為未確認完成時允許再次觸發
      autoPackingTriggeredRef.current = false;
    }
  }, [isAllConfirmed]);

  return (
    <div style={{ padding: '24px' }}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      {/* KPI 概覽 - 僅在掃描操作顯示 */}
      {kpi && (
        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
          <Row gutter={16}>
            <Col flex={1}>
              <Card bordered hoverable>
                <Statistic title="處理中 (Pick 總數)" value={kpi.processing} prefix={<InboxOutlined />} />
              </Card>
            </Col>
            <Col flex={1}>
              <Card bordered hoverable>
                <Statistic title="配貨中 (Pick 未完)" value={kpi.allocating} />
              </Card>
            </Col>
            <Col flex={1}>
              <Card bordered hoverable>
                <Statistic title="核對中 (Pick 完成)" value={kpi.verifying} />
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <ScanOutlined /> 揀貨掃描操作
        </Title>
        <Text type="secondary">
          批次編號: {currentBatch.batchPickingNumber} | 批次條碼: {currentBatch.batchPickingBarcode}
        </Text>
      </div>

      {/* Progress Steps */}
      <Card style={{ marginBottom: '24px' }}>
        <Steps current={getCurrentStep()} style={{ marginBottom: '24px' }}>
          <Step
            title="揀貨作業"
            icon={<BarcodeOutlined />}
          />
          <Step
            title="核對確認"
            icon={<CheckCircleOutlined />}
          />
        </Steps>
        
        {currentWaybillOrder ? (
          <Alert
            message={`已掃描運單：${currentWaybillOrder}`}
            description={`請掃描該運單的產品條碼（共 ${waybillOrderProducts.length} 個產品）`}
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        ) : (
          <Alert
            message="請先掃描運單條碼"
            description="掃描 Print Label 上的運單條碼，然後掃描產品條碼"
            type="warning"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}
      </Card>

        {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="總數量" 
              value={totalQuantity}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="已拣數量" 
              value={pickedQuantity}
              valueStyle={{ color: '#722ed1' }}
              prefix={<CheckCircleOutlined />}
            />
            <Progress 
              percent={totalQuantity > 0 ? Math.round((pickedQuantity / totalQuantity) * 100) : 0}
              size="small"
              status="active"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="已確認數量" 
              value={confirmedQuantity}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
            <Progress 
              percent={totalQuantity > 0 ? Math.round((confirmedQuantity / totalQuantity) * 100) : 0}
              size="small"
              status="success"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="差異項目" 
              value={discrepancyCount}
              valueStyle={{ color: discrepancyCount > 0 ? '#ff4d4f' : '#52c41a' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={16}>
        <Col span={16}>
          {/* Scan Input */}
          <Card
            title={
              <Space>
                <ScanOutlined />
                <span>掃描區域</span>
                <Tag color={getStatusColor(currentBatch.status)}>
                  {getStatusText(currentBatch.status)}
                </Tag>
              </Space>
            }
            style={{ marginBottom: '16px' }}
          >
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <BarcodeOutlined 
                style={{ 
                  fontSize: '64px', 
                  color: scanning ? '#1890ff' : '#ccc',
                  marginBottom: '16px'
                }} 
              />
              <div style={{ marginBottom: '24px' }}>
                <Input
                  ref={scanInputRef}
                  placeholder={
                    '掃描條碼...'
                  }
                  size="large"
                  prefix={<ScanOutlined />}
                  onPressEnter={(e) => {
                    const value = (e.target as HTMLInputElement).value;
                    handleScan(value);
                    (e.target as HTMLInputElement).value = '';
                    setManualScanValue('');
                  }}
                  value={manualScanValue}
                  onChange={(e) => setManualScanValue(e.target.value)}
                  autoFocus
                  style={{ maxWidth: '400px' }}
                />
              </div>
            </div>
          </Card>

          {/* Item List */}
          <Card
            title={
              <Space>
                <InboxOutlined />
                <span>產品列表</span>
                <Badge count={localItems.length} />
              </Space>
            }
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Tag color={isAllConfirmed ? 'green' : (isAllScanned ? 'gold' : 'orange')}>
                {isAllConfirmed ? '已確認完成，自動完成撮貨' : (isAllScanned ? '已拣滿，請再次掃描確認' : '尚未拣滿，請繼續掃描')}
              </Tag>
            </div>
            <List
              dataSource={localItems}
              renderItem={(item) => (
              <List.Item
                  ref={(el) => { if (el) itemRowRefs.current[item.productId] = el; }}
                  style={{
                    background: item.productId === lastScannedId ? '#e6f7ff' : (
                      item.hasDiscrepancy ? '#fff2f0' : 
                      item.confirmedQuantity === item.totalQuantity ? '#f6ffed' : 
                      item.pickedQuantity > 0 ? '#f9f0ff' : '#fafafa'
                    ),
                    marginBottom: '8px',
                    padding: '16px',
                    borderRadius: '6px',
                    border: item.productId === lastScannedId ? '1px solid #1890ff' : '1px solid #e8e8e8',
                    boxShadow: item.productId === lastScannedId ? '0 0 0 2px rgba(24,144,255,0.15)' : 'none'
                  }}
                  actions={[
                    <Space key="actions">
                      {item.productId === lastScannedId && (
                        <Tag color="blue">最新掃描</Tag>
                      )}
                      {item.hasDiscrepancy && (
                        <Tag color="red" icon={<WarningOutlined />}>
                          差異
                        </Tag>
                      )}
                      {item.confirmedQuantity === item.totalQuantity && !item.hasDiscrepancy && (
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                          完成
                        </Tag>
                      )}
                    </Space>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <span style={{ fontWeight: 'bold' }}>{item.productSku}</span>
                        <span>{item.productNameCn}</span>
                        <Tag>{item.productBarcode}</Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: '8px' }}>
                          <Text type="secondary">{item.productNameEn}</Text>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          位置: {item.locationFrom} → {item.locationTo}
                        </div>
                        {item.lotNumbers.length > 0 && (
                          <div style={{ marginBottom: '8px' }}>
                            批次號: {item.lotNumbers.join(', ')}
                          </div>
                        )}
                        {item.serialNumbers.length > 0 && (
                          <div style={{ marginBottom: '8px' }}>
                            序列號: {item.serialNumbers.join(', ')}
                          </div>
                        )}
                        <Row gutter={16}>
                          <Col span={8}>
                            <div>
                              <Text strong>需求: </Text>
                              <Text>{item.totalQuantity}</Text>
                            </div>
                          </Col>
                          <Col span={8}>
                            <div>
                              <Text strong>已拣: </Text>
                              <InputNumber
                                size="small"
                                min={0}
                                max={item.totalQuantity}
                                value={item.pickedQuantity}
                                onChange={(value) => handleManualQuantityUpdate(item.productId, 'picked', value || 0)}
                                style={{ width: '80px' }}
                              />
                            </div>
                          </Col>
                          <Col span={8}>
                            <div>
                              <Text strong>確認: </Text>
                              <InputNumber
                                size="small"
                                min={0}
                                max={item.pickedQuantity}
                                value={item.confirmedQuantity}
                                onChange={(value) => handleManualQuantityUpdate(item.productId, 'confirmed', value || 0)}
                                style={{ width: '80px' }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col span={8}>
          {/* Scan History */}
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                <span>掃描歷史</span>
                <Badge count={scanHistory.length} />
              </Space>
            }
            extra={
              <Tooltip title="清空掃描歷史">
                <Popconfirm
                  title="確認清空掃描歷史？"
                  okText="清空"
                  cancelText="取消"
                  onConfirm={handleClearScanHistory}
                >
                  <Button type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
                </Popconfirm>
              </Tooltip>
            }
          >
            <Timeline
              mode="left"
              style={{ maxHeight: '300px', overflowY: 'auto', wordBreak: 'break-word' , padding: '2px'}}
            >
              {scanHistory.map((scan, index) => (
                <Timeline.Item
                  key={index}
                  color={scan.success ? 'green' : 'red'}
                  dot={
                    scan.success ? 
                    <CheckCircleOutlined style={{ color: 'green', fontSize: '16px' }} /> :
                    <ExclamationCircleOutlined style={{ color: 'red', fontSize: '20px' }} />
                  }
                >
                  <div style={{ fontSize: scan.success ? '12px' : '14px', padding: '4px' }}>
                    <div
                      style={{
                        fontWeight: 'bold',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%',
                        fontSize: scan.success ? '13px' : '15px'
                      }}
                    >
                      {scan.barcode}
                    </div>
                    <div style={{ color: '#666', fontSize: '11px', marginTop: '2px' }}>
                      {moment(scan.timestamp).format('HH:mm:ss')}
                    </div>
                    {scan.error && (
                      <Alert
                        message={
                          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                            ⚠️ 掃描失敗
                          </div>
                        }
                        description={
                          <div style={{ fontSize: '13px', whiteSpace: 'pre-line' }}>
                            {scan.error}
                          </div>
                        }
                        type="error"
                        showIcon
                        icon={<ExclamationCircleOutlined style={{ fontSize: '20px' }} />}
                        style={{ 
                          marginTop: '8px',
                          padding: '12px',
                          border: '2px solid #ff4d4f'
                        }}
                      />
                    )}
                    {scan.data && (
                      <div style={{ 
                        color: 'green', 
                        wordBreak: 'break-word', 
                        whiteSpace: 'normal', 
                        overflowWrap: 'anywhere',
                        marginTop: '4px',
                        fontSize: '12px'
                      }}>
                        ✓ {scan.data.productNameCn || scan.data.orderNumber || '掃描成功'}
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>
      </Row>

      {/* Discrepancy Modal */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            <span>處理差異</span>
          </Space>
        }
        open={discrepancyModalVisible}
        onCancel={() => setDiscrepancyModalVisible(false)}
        footer={null}
        width={600}
      >
        {currentDiscrepancy && (
          <div>
            <Alert
              message="檢測到數量差異"
              description={`產品 ${currentDiscrepancy.productName} 發生數量差異，需要處理後才能繼續`}
              type="error"
              showIcon
              style={{ marginBottom: '24px' }}
            />
            
            <Descriptions
              title="差異詳情"
              bordered
              column={2}
              style={{ marginBottom: '24px' }}
            >
              <Descriptions.Item label="產品SKU">
                {currentDiscrepancy.productSku}
              </Descriptions.Item>
              <Descriptions.Item label="產品名稱">
                {currentDiscrepancy.productName}
              </Descriptions.Item>
              <Descriptions.Item label="發貨數量">
                <Text style={{ color: '#1890ff', fontWeight: 'bold' }}>
                  {currentDiscrepancy.expectedQuantity}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="確認數量">
                <Text style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                  {currentDiscrepancy.actualQuantity}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="差異類型" span={2}>
                <Tag color="red">
                  {currentDiscrepancy.discrepancyType === 'shortage' ? '數量不足' : '數量超出'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleResolveDiscrepancy}
            >
              <Form.Item
                label="差異原因"
                name="reason"
                rules={[{ required: true, message: '請輸入差異原因' }]}
              >
                <Input.TextArea 
                  rows={3} 
                  placeholder="請描述造成差異的原因..."
                />
              </Form.Item>

              <Form.Item
                label="處理方案"
                name="resolution"
                rules={[{ required: true, message: '請輸入處理方案' }]}
              >
                <Input.TextArea 
                  rows={3} 
                  placeholder="請描述如何處理這個差異..."
                />
              </Form.Item>

              <div style={{ textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => setDiscrepancyModalVisible(false)}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit">
                    確認處理
                  </Button>
                </Space>
              </div>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BarcodeScanningInterface;
